import { useTranslation as usePublicI18n } from "react-i18next";
import { publicText, publicTemplate } from "./publicText";
import React, { useMemo, useEffect } from "react";
import { AimOutlined, AreaChartOutlined, ClockCircleOutlined, DownloadOutlined, ExperimentOutlined, FieldTimeOutlined, LineChartOutlined, NodeIndexOutlined, RiseOutlined, ThunderboltOutlined } from "@ant-design/icons";
import PublicLayout from "./PublicLayout";
import { CASE04 } from "./case04Data";
import { formatNumber, Reveal, useDocumentTitle } from "./components/PageUtilities";
import { CaseNavigation, CaseStudyBreadcrumb, DataTable, Figure, KeyFinding, MethodGrid, MetricCard, ScientificNote, SectionHeader } from "./components/ScientificComponents";
import { PhaseRibbon, ScientificLineChart } from "./components/ScientificCharts";
import { AggregationComparison, CorrelationCards, DataWorkflow, HourlyScatterPlot, LocalNavigation, MetricComparisonMatrix, MetricDefinitionGrid, RelatedCases, RepresentativeMetricCards, ScatterPlot } from "./CaseStudy04Blocks";
import "./case04.css";
import { trackEvent } from "../FirebaseConfig";
function CaseStudy04() {
  usePublicI18n();
  useDocumentTitle(publicText("Case Study 04｜18,582個の気孔から、何が見える？"));
  useEffect(() => {
    trackEvent("case_study_view", {
      case_study_id: "04"
    });
  }, []);
  const labels = CASE04.rows.map(row => row.timeLabel);
  const values = key => CASE04.rows.map(row => row[key]);
  const tableRows = useMemo(() => CASE04.rows.map(row => ({
    ...row,
    sideLabel: row.side === "east" ? "午前・樹東面" : row.side === "west" ? "午後・樹西面" : "12時・切替",
    openRateText: row.openRate.toFixed(1),
    apertureText: row.aperture.toFixed(2),
    aperturePooledText: row.aperturePooled.toFixed(2),
    poreAreaText: row.poreArea.toFixed(1),
    aspectRatioText: row.aspectRatio.toFixed(3),
    solarText: row.solar.toFixed(0),
    temperatureText: row.temperature.toFixed(1),
    vpdText: row.vpd.toFixed(2),
    co2Text: row.co2.toFixed(0)
  })), []);
  return <PublicLayout>
      <article className="science-case-page case-theme-04">
        <header className="science-case-hero science-case-hero--data case04-field-hero">
          <div className="science-case-hero__grid" />
          <div className="science-orb orb-b" />
          <div className="science-orb orb-c" />

          <div className="case-container">
            <CaseStudyBreadcrumb current="04" />

            <div className="science-case-hero__layout">
              <Reveal className="science-case-hero__copy">
                <div className="science-case-id">{publicText("CASE STUDY 04")}</div>
                <span className="science-case-domain">{publicText("STOMATAL BIG DATA / ENVIRONMENT-LINKED ANALYSIS")}</span>

                <h1>{publicText("18,582\u500B\u306E\u6C17\u5B54\u304B\u3089\u3001")}<br />
                  <em>{publicText("\u4F55\u304C\u898B\u3048\u308B\uFF1F")}</em>
                </h1>

                <p>{publicText(CASE04.lead)}</p>

                <div className="science-case-tags">
                  <span>{publicText(CASE04.field.date)}</span>
                  <span>{publicText(CASE04.field.window)}</span>
                  <span>{publicText("commercial tomato greenhouse")}</span>
                  <span>{publicText("Open / Closed")}</span>
                  <span>{publicText("Aperture / Area / Short-Long")}</span>
                  <span>{publicText("image \u2192 hour \u2192 environment")}</span>
                </div>

                <div className="science-case-hero__headline-result is-four case04-hero-metrics">
                  <div>
                    <small>{publicText("IMAGES")}</small>
                    <strong>{publicText(formatNumber(CASE04.totals.images, 0))}</strong>
                  </div>
                  <div>
                    <small>{publicText("STOMATA")}</small>
                    <strong>{publicText(formatNumber(CASE04.totals.stomata, 0))}</strong>
                  </div>
                  <div>
                    <small>{publicText("MORPHOLOGY")}</small>
                    <strong>{publicText(CASE04.totals.validMorphologyImages)}<small>{publicText("images")}</small></strong>
                  </div>
                  <div>
                    <small>{publicText("IRRIGATION")}</small>
                    <strong>{publicText(CASE04.totals.irrigationCycles)}<small>{publicText("cycles")}</small></strong>
                  </div>
                </div>

              </Reveal>

              <Reveal className="science-case-hero__media case04-hero-media" delay={120}>
                <div className="case04-hero-screen">
                  <div className="case04-hero-screen__topbar">
                    <span /><span /><span />
                    <small>{publicText("StomataScope / Big-data analysis workflow")}</small>
                  </div>
                  <img src={CASE04.images.hero} alt={publicText("18,582個の気孔データを画像・時間・環境へ統合する解析ダッシュボード")} />
                </div>
                <div className="case04-hero-badge is-first">
                  <small>{publicText("RAW DETECTIONS")}</small>
                  <strong>{publicText("18,582")}</strong>
                  <span>{publicText("stomata")}</span>
                </div>
                <div className="case04-hero-badge is-last">
                  <small>{publicText("ANALYSIS LEVELS")}</small>
                  <strong>{publicText("3")}</strong>
                  <span>{publicText("stoma / image / hour")}</span>
                </div>
              </Reveal>
            </div>
          </div>
        </header>

        <LocalNavigation />

        <section id="question" className="science-section science-question-section case04-anchor">
          <div className="study-content">
            <SectionHeader index="01" eyebrow="STUDY QUESTION" title={publicText("大量に測ったあと、どう植物の情報へ変えるか。")} description={publicText(CASE04.question)} />

            <KeyFinding title={publicText("問いに合わせて、指標とデータの粒度を変える。")}>
              <p>{publicText("\u6C17\u5B54\u5358\u4F4D\u3067\u306F\u5206\u5E03\u304C\u898B\u3048\u3001\u753B\u50CF\u5358\u4F4D\u3067\u306F\u8449\u30FB\u8996\u91CE\u3054\u3068\u306E\u3070\u3089\u3064\u304D\u304C\u6B8B\u308A\u3001 1\u6642\u9593\u5358\u4F4D\u3067\u306F\u65E5\u5468\u30C8\u30EC\u30F3\u30C9\u304C\u898B\u3048\u307E\u3059\u3002Open\u7387\u3001Aperture\u3001Pore Area\u3001 Short/Long\u3082\u3001\u305D\u308C\u305E\u308C\u7570\u306A\u308B\u554F\u3044\u306B\u7B54\u3048\u308B\u6307\u6A19\u3067\u3059\u3002")}</p>
            </KeyFinding>
          </div>
        </section>

        <section id="dataset" className="science-section is-soft case04-anchor">
          <div className="case-container">
            <SectionHeader index="02" eyebrow="FIELD DATASET" title={publicText("538画像・18,582気孔と、約10.4時間の環境・灌水データ。")} description={publicTemplate(489, [CASE04.field.date, CASE04.field.window])} />

            <Reveal className="science-metric-grid is-four">
              <MetricCard label={publicText("撮影画像")} value={formatNumber(CASE04.totals.images, 0)} unit="画像" caption={publicText("07:00:21〜17:24:39")} tone="green" icon={<ExperimentOutlined />} />
              <MetricCard label={publicText("検出気孔")} value={formatNumber(CASE04.totals.stomata, 0)} unit="個" caption={publicText(`Open ${formatNumber(CASE04.totals.open, 0)} / Closed ${formatNumber(CASE04.totals.closed, 0)}`)} tone="blue" icon={<LineChartOutlined />} />
              <MetricCard label={publicText("形状解析画像")} value={formatNumber(CASE04.totals.validMorphologyImages, 0)} unit="画像" caption={publicText("有効なOpen気孔形状を取得")} tone="purple" icon={<NodeIndexOutlined />} />
              <MetricCard label={publicText("灌水サイクル")} value={formatNumber(CASE04.totals.irrigationCycles, 0)} unit="回" caption={publicText("6バルブの近接記録を1回へ集約")} tone="orange" icon={<ClockCircleOutlined />} />
            </Reveal>

            <div className="case04-field-layout">
              <Figure number="01" title={publicText("商業栽培トマト圃場での連続測定")} src={CASE04.images.field} alt={publicText("商業栽培トマト圃場におけるStomataScopeの測定風景")} contain caption={publicText("Case Study 03で示した大量計測を、Case Study 04では環境データと統合して解析しました。")} className="case04-field-figure" />

              <MethodGrid items={[{
              label: "気孔単位データ",
              value: "18,582 detections",
              note: "Open / Closed・形状・撮影時刻・画像ID"
            }, {
              label: "画像単位データ",
              value: "519 image summaries",
              note: "Open率・Aperture・Area・Short/Long"
            }, {
              label: "環境データ",
              value: "1-minute interval",
              note: "日射・温度・RH・VPD・CO₂"
            }, {
              label: "解析",
              value: "time series / regression / event study",
              note: "粒度と指標を変えて比較"
            }]} />
            </div>
          </div>
        </section>

        <section id="workflow" className="science-section case04-anchor">
          <div className="case-container">
            <SectionHeader index="03" eyebrow="DATA PROCESSING WORKFLOW" title={publicText("RAWデータから、栽培で読める情報へ。")} description={publicText("気孔単位の検出結果を、そのまま平均するのではなく、画像・時刻・環境へ段階的に加工しました。")} />

            <DataWorkflow />

            <ScientificNote title={publicText("形状解析とOpen率では、使うデータが異なる")} tone="info">
              <p>{publicText("Open\u7387\u306FClosed\u3092\u542B\u3080\u516818,582\u6C17\u5B54\u3092\u4F7F\u7528\u3057\u307E\u3059\u3002Aperture\u3001Pore Area\u3001Short/Long\u306F\u3001 \u6709\u52B9\u306A\u958B\u53E3\u90E8\u304C\u691C\u51FA\u3055\u308C\u305FOpen\u6C17\u5B54\u3092\u5BFE\u8C61\u306B\u753B\u50CF\u4E2D\u592E\u5024\u3092\u7B97\u51FA\u3057\u307E\u3059\u3002 Open\u6C17\u5B54\u304C\u5F97\u3089\u308C\u306A\u304B\u3063\u305F\u753B\u50CF\u306F\u5F62\u72B6\u6307\u6A19\u306E\u96C6\u8A08\u304B\u3089\u9664\u5916\u3057\u3001Open\u7387\u306E\u96C6\u8A08\u306B\u306F\u6B8B\u3057\u307E\u3059\u3002")}</p>
            </ScientificNote>
          </div>
        </section>

        <section id="metrics" className="science-section is-soft case04-anchor">
          <div className="case-container">
            <SectionHeader index="04" eyebrow="FOUR RESPONSE METRICS" title={publicText("Open / Closedの先まで見る。")} description={publicText("同じ画像から、気孔集団の状態・開き幅・面積・形状を別々の指標として取り出します。")} />

            <MetricDefinitionGrid />

            <div className="case04-subsection-head">
              <span>{publicText("07:00 / 13:00 / 17:00")}</span>
              <h3>{publicText("1\u65E5\u306E\u4EE3\u88683\u6642\u70B9\u3092\u30014\u3064\u306E\u6307\u6A19\u3067\u6BD4\u8F03\u3002")}</h3>
              <p>{publicText("Open\u7387\u3060\u3051\u3067\u306A\u304F\u3001Open\u6C17\u5B54\u304C\u3069\u308C\u3060\u3051\u958B\u304D\u3001\u958B\u53E3\u90E8\u304C\u3069\u306E\u3088\u3046\u306A\u5F62\u306B\u306A\u3063\u305F\u304B\u3092\u4E26\u3079\u307E\u3057\u305F\u3002")}</p>
            </div>

            <RepresentativeMetricCards />

            <ScientificNote title={publicText("“Open”でも、開き方は同じではない")} tone="success">
              <p>{publicText("13\u6642\u53F0\u306FOpen\u738795.9%\u306B\u52A0\u3048\u3001Aperture 6.48 \xB5m\u3001Pore Area 85.6 \xB5m\xB2\u3001 Short/Long 0.352\u3067\u3057\u305F\u300214\u301C15\u6642\u53F0\u306FOpen\u7387\u304C\u7D0491%\u3067\u3082\u3001 \u5F62\u72B6\u6307\u6A19\u306F13\u6642\u53F0\u3088\u308A\u5C0F\u3055\u304F\u306A\u3063\u3066\u3044\u307E\u3059\u3002")}</p>
            </ScientificNote>
          </div>
        </section>

        <section id="sampling" className="science-section case04-anchor">
          <div className="case-container">
            <SectionHeader index="05" eyebrow="TREE-FACE SAMPLING" title={publicText("ハウス内の東西エリアではなく、樹の東面・西面を切り替えた。")} description={publicText(CASE04.field.sampling)} />

            <Figure number="02" title={publicText("太陽の移動に合わせた樹面サンプリング")} src={CASE04.images.sampling} alt={publicText("午前は各樹の東面、午後は各樹の西面で受光葉を測定した模式図")} contain caption={publicText("各樹の受光面を追って測定。ハウス内を東西のエリアに分けた比較ではありません。")} className="science-figure--analysis case04-sampling-figure" />

            <ScientificNote title={publicText("午前・午後の比較で言える範囲")} tone="caution">
              <p>{publicText("\u5348\u524D\u3068\u5348\u5F8C\u3067\u306F\u6E2C\u5B9A\u3057\u305F\u6A39\u9762\u30FB\u8449\u30FB\u500B\u4F53\u304C\u7570\u306A\u308A\u307E\u3059\u3002\u305D\u306E\u305F\u3081\u3001 \u540C\u4E00\u8449\u306E\u30D2\u30B9\u30C6\u30EA\u30B7\u30B9\u3084\u300C\u5348\u5F8C\u3060\u304B\u3089\u5909\u5316\u3057\u305F\u300D\u3068\u3044\u3046\u56E0\u679C\u3092\u793A\u3059\u6BD4\u8F03\u3067\u306F\u3042\u308A\u307E\u305B\u3093\u3002 \u672C\u30DA\u30FC\u30B8\u3067\u306F\u3001\u4EE3\u8868\u74B0\u5883\u5024\u304C\u8FD1\u304F\u3066\u3082\u5B9F\u6E2C\u3055\u308C\u305F\u690D\u7269\u5FDC\u7B54\u304C\u4E00\u69D8\u3067\u306F\u306A\u304B\u3063\u305F\u3053\u3068\u3092\u793A\u3057\u307E\u3059\u3002")}</p>
            </ScientificNote>
          </div>
        </section>

        <section id="daily" className="science-section is-dark case04-anchor">
          <div className="case-container">
            <SectionHeader index="06" eyebrow="DAILY RESPONSE" title={publicText("Open率・開度・面積・形状を、同じ時間軸で見る。")} description={publicText("形状指標は画像別中央値を1時間内で平均し、Open率は時間帯内の全気孔を合算しました。")} />

            <div className="science-chart-grid">
              <Reveal>
                <ScientificLineChart labels={labels} series={[{
                name: "Aperture",
                values: values("aperture")
              }]} title={publicText("Stomatal Aperture")} subtitle={publicText("画像別Open気孔中央値 → 時間内平均")} unit="µm" yMin={0} yMax={7} valueDigits={2} footer="13時台に6.48 µmで最大。" />
              </Reveal>
              <Reveal delay={80}>
                <ScientificLineChart labels={labels} series={[{
                name: "Open率",
                values: values("openRate")
              }]} title={publicText("Open率")} subtitle={publicText("時間帯内の全検出気孔を合算")} unit="%" yMin={0} yMax={100} valueDigits={1} footer="10〜15時台は91〜96%。夕方に低下。" />
              </Reveal>
              <Reveal delay={160}>
                <ScientificLineChart labels={labels} series={[{
                name: "Pore Area",
                values: values("poreArea")
              }]} title={publicText("Pore Area")} subtitle={publicText("画像別Open気孔面積中央値 → 時間内平均")} unit="µm²" yMin={0} yMax={95} valueDigits={1} footer="13時台に85.6 µm²で最大。" />
              </Reveal>
              <Reveal delay={240}>
                <ScientificLineChart labels={labels} series={[{
                name: "Short / Long",
                values: values("aspectRatio")
              }]} title={publicText("Pore Aspect Ratio")} subtitle={publicText("短軸 ÷ 長軸 / 画像中央値 → 時間内平均")} unit="" yMin={0.1} yMax={0.42} valueDigits={3} footer="開口部が広がるにつれて比率も上昇。" />
              </Reveal>
            </div>

            <PhaseRibbon phases={CASE04.phases} />
          </div>
        </section>

        <section id="granularity" className="science-section case04-anchor">
          <div className="case-container">
            <SectionHeader index="07" eyebrow="AGGREGATION LEVELS" title={publicText("同じデータでも、まとめ方で見え方が変わる。")} description={publicText("気孔単位・画像単位・1時間単位を比較し、どの問いにどの粒度が合うかを整理しました。")} />

            <AggregationComparison />

            <ScientificNote title={publicText("集計すると傾向は明瞭になるが、ばらつきは見えにくくなる")} tone="info">
              <p>{publicText("1\u6642\u9593\u96C6\u8A08\u306F\u65E5\u5468\u5909\u5316\u3084\u74B0\u5883\u3068\u306E\u5BFE\u5FDC\u3092\u898B\u3084\u3059\u304F\u3057\u307E\u3059\u3002\u4E00\u65B9\u3001\u753B\u50CF\u5358\u4F4D\u306E\u6563\u5E03\u56F3\u306B\u306F\u3001 \u540C\u3058\u74B0\u5883\u5024\u3067\u3082\u7570\u306A\u308B\u690D\u7269\u5FDC\u7B54\u304C\u6B8B\u308A\u307E\u3059\u3002\u96C6\u8A08\u524D\u30FB\u96C6\u8A08\u5F8C\u3092\u4E21\u65B9\u898B\u308B\u3053\u3068\u304C\u91CD\u8981\u3067\u3059\u3002")}</p>
            </ScientificNote>
          </div>
        </section>

        <section id="environment" className="science-section is-soft case04-anchor">
          <div className="case-container">
            <SectionHeader index="08" eyebrow="ENVIRONMENTAL CONTEXT" title={publicText("日射・温度・VPD・CO₂を、気孔と同じ時間帯で整理。")} description={publicText("植物側の応答を解釈するため、複数の環境項目を並べて確認しました。")} />

            <div className="science-chart-grid">
              <Reveal>
                <ScientificLineChart labels={labels} series={[{
                name: "日射量",
                values: values("solar")
              }]} title={publicText("日射量")} subtitle={publicText("撮影時刻に対応した環境値の時間帯平均")} unit="" yMin={0} yMax={700} valueDigits={0} />
              </Reveal>
              <Reveal delay={80}>
                <ScientificLineChart labels={labels} series={[{
                name: "温度",
                values: values("temperature")
              }]} title={publicText("ハウス温度")} subtitle={publicText("撮影時刻に対応した時間帯平均")} unit="°C" yMin={10} yMax={38} valueDigits={1} />
              </Reveal>
              <Reveal delay={160}>
                <ScientificLineChart labels={labels} series={[{
                name: "VPD",
                values: values("vpd")
              }]} title={publicText("VPD")} subtitle={publicText("温度・相対湿度から算出")} unit="kPa" yMin={0} yMax={2.2} valueDigits={2} />
              </Reveal>
              <Reveal delay={240}>
                <ScientificLineChart labels={labels} series={[{
                name: "CO₂",
                values: values("co2")
              }]} title={publicText("CO₂濃度")} subtitle={publicText("撮影時刻に対応した時間帯平均")} unit="ppm" yMin={330} yMax={500} valueDigits={0} />
              </Reveal>
            </div>

            <ScientificNote title={publicText("環境項目は互いにも連動する")} tone="info">
              <p>{publicText("\u5348\u524D\u4E2D\u306F\u65E5\u5C04\u3001\u6E29\u5EA6\u3001VPD\u304C\u540C\u6642\u306B\u4E0A\u6607\u3057\u307E\u3059\u3002\u5358\u7D14\u76F8\u95A2\u304C\u9AD8\u304F\u3066\u3082\u3001 \u305D\u306E\u9805\u76EE\u3060\u3051\u304C\u5F62\u72B6\u5909\u5316\u306E\u539F\u56E0\u3068\u306F\u9650\u308A\u307E\u305B\u3093\u3002")}</p>
            </ScientificNote>
          </div>
        </section>

        <section id="relationships" className="science-section case04-anchor">
          <div className="case-container">
            <SectionHeader index="09" eyebrow="IMAGE-LEVEL RELATIONSHIPS" title={publicText("画像単位では、関係とばらつきの両方が見える。")} description={publicText("各画像のAperture中央値に撮影時刻の環境値を対応。12時台を除き、午前・樹東面と午後・樹西面を色分けしました。")} />

            <div className="case04-scatter-grid">
              <ScatterPlot metric="solar" />
              <ScatterPlot metric="temperature" />
              <ScatterPlot metric="vpd" />
            </div>

            <CorrelationCards />

            <div className="case04-model-summary">
              <div>
                <small>{publicText("MULTIVARIATE EXPLORATION")}</small>
                <strong>{publicText("R\xB2 = ")}{publicText(CASE04.multivariate.r2.toFixed(2))}</strong>
                <span>{publicText("\u65E5\u5C04\u30FBVPD\u30FB\u6E29\u5EA6\u30FBCO\u2082\u3092\u540C\u6642\u6295\u5165 / n = ")}{publicText(CASE04.multivariate.n)}</span>
              </div>
              <dl>
                <div><dt>{publicText("\u6E29\u5EA6 \u03B2")}</dt><dd>{publicText(CASE04.multivariate.standardizedBeta.temperature.toFixed(2))}</dd></div>
                <div><dt>{publicText("VPD \u03B2")}</dt><dd>{publicText(CASE04.multivariate.standardizedBeta.vpd.toFixed(2))}</dd></div>
                <div><dt>{publicText("\u65E5\u5C04 \u03B2")}</dt><dd>{publicText(CASE04.multivariate.standardizedBeta.solar.toFixed(2))}</dd></div>
                <div><dt>{publicText("CO\u2082 \u03B2")}</dt><dd>{publicText(CASE04.multivariate.standardizedBeta.co2.toFixed(2))}</dd></div>
              </dl>
            </div>

            <ScientificNote title={publicText("画像は完全に独立した反復ではない")} tone="caution">
              <p>{publicText("\u540C\u3058\u65E5\u306E\u9023\u7D9A\u6642\u7CFB\u5217\u3067\u3042\u308A\u3001\u8AAC\u660E\u5909\u6570\u540C\u58EB\u3082\u5F37\u304F\u95A2\u9023\u3057\u307E\u3059\u3002 \u76F8\u95A2\u30FB\u56DE\u5E30\u30FB\u6A19\u6E96\u5316\u4FC2\u6570\u306F\u3053\u306E\u65E5\u306E\u30C7\u30FC\u30BF\u69CB\u9020\u3092\u7406\u89E3\u3059\u308B\u63A2\u7D22\u5024\u3067\u3059\u3002")}</p>
            </ScientificNote>
          </div>
        </section>

        <section id="hourly-relationships" className="science-section is-soft case04-anchor">
          <div className="case-container">
            <SectionHeader index="10" eyebrow="HOURLY RELATIONSHIPS" title={publicText("1時間ごとにまとめると、日周トレンドがより明瞭になる。")} description={publicText("全体・午前の樹東面・午後の樹西面に分け、3本の近似式を表示しました。12時台は切替時間帯として回帰から除外しています。")} />

            <div className="case04-hourly-grid">
              <HourlyScatterPlot metric="solar" />
              <HourlyScatterPlot metric="temperature" />
              <HourlyScatterPlot metric="vpd" />
            </div>

            <KeyFinding title={publicText("Apertureの1時間集計では、VPDとの対応が最も強く見えた。")}>
              <p>{publicText("\u5168\u4F53\u306ER\xB2\u306F\u65E5\u5C040.66\u3001\u6E29\u5EA60.79\u3001VPD0.89\u3067\u3057\u305F\u3002 \u305F\u3060\u3057\u30011\u6642\u9593\u96C6\u8A08\u306F10\u70B9\u306E\u307F\u3067\u3001\u5E73\u6ED1\u5316\u306B\u3088\u3063\u3066R\xB2\u304C\u9AD8\u304F\u306A\u308A\u3084\u3059\u3044\u70B9\u306B\u6CE8\u610F\u304C\u5FC5\u8981\u3067\u3059\u3002")}</p>
            </KeyFinding>
          </div>
        </section>

        <section id="metric-comparison" className="science-section case04-anchor">
          <div className="case-container">
            <SectionHeader index="11" eyebrow="METRIC × GRANULARITY COMPARISON" title={publicText("どの指標が環境変化を捉えやすいかも比較。")} description={publicText("Aperture、Pore Area、Short/Longについて、画像単位と1時間単位のR²を並べました。")} />

            <MetricComparisonMatrix />

            <div className="case04-comparison-findings">
              <article>
                <span>{publicText("APERTURE")}</span>
                <strong>{publicText("VPD\uFF1A0.51 \u2192 0.89")}</strong>
                <p>{publicText("\u958B\u304D\u5E45\u306E\u65E5\u5468\u5909\u5316\u306F\u30011\u6642\u9593\u96C6\u8A08\u3067VPD\u3068\u306E\u5BFE\u5FDC\u304C\u660E\u77AD\u3002")}</p>
              </article>
              <article>
                <span>{publicText("PORE AREA")}</span>
                <strong>{publicText("VPD\uFF1A0.50 \u2192 0.90")}</strong>
                <p>{publicText("\u958B\u53E3\u9818\u57DF\u5168\u4F53\u3082\u3001Aperture\u3068\u8FD1\u3044\u65E5\u5468\u30D1\u30BF\u30FC\u30F3\u3002")}</p>
              </article>
              <article>
                <span>{publicText("SHORT / LONG")}</span>
                <strong>{publicText("\u65E5\u5C04\uFF1A0.44 \u2192 0.85")}</strong>
                <p>{publicText("\u958B\u53E3\u90E8\u306E\u5F62\u72B6\u6BD4\u306F\u30011\u6642\u9593\u96C6\u8A08\u3067\u65E5\u5C04\u3068\u306E\u5BFE\u5FDC\u304C\u5F37\u304F\u898B\u3048\u305F\u3002")}</p>
              </article>
            </div>

            <ScientificNote title={publicText("R²の高低だけで指標の優劣は決めない")} tone="caution">
              <p>{publicText("\u6307\u6A19\u306F\u7B54\u3048\u308B\u554F\u3044\u304C\u7570\u306A\u308A\u307E\u3059\u3002Aperture\u306F\u958B\u304D\u5E45\u3001Area\u306F\u958B\u53E3\u9818\u57DF\u3001 Short/Long\u306F\u5F62\u72B6\u3067\u3059\u3002\u307E\u305F\u30011\u6642\u9593\u96C6\u8A08\u306Fn=10\u306E\u63A2\u7D22\u5024\u3067\u3042\u308A\u3001 \u753B\u50CF\u5358\u4F4D\u306E\u3070\u3089\u3064\u304D\u3092\u6B8B\u3057\u305F\u89E3\u6790\u3068\u4F75\u305B\u3066\u89E3\u91C8\u3057\u307E\u3059\u3002")}</p>
            </ScientificNote>
          </div>
        </section>

        <section id="irrigation" className="science-section is-dark case04-anchor">
          <div className="case-container">
            <SectionHeader index="12" eyebrow="IRRIGATION EVENT STUDY" title={publicText("20回の灌水があっても、気孔は毎回同じようには反応しなかった。")} description={publicText("灌水時刻を0分として画像を最も近いイベントへ割り当て、前後10分を比較しました。")} />

            <Figure number="03" title={publicText("灌水サイクルとApertureの日周変化")} src={CASE04.images.irrigation} alt={publicText("20回の灌水サイクルと1時間ごとの気孔開度を重ねた時系列図")} contain caption={publicText("全イベントに共通する『灌水直後のAperture上昇』は確認されませんでした。")} className="science-figure--analysis case04-irrigation-figure" />

            <Reveal className="science-metric-grid is-four case04-irrigation-metrics">
              <MetricCard label={publicText("灌水サイクル")} value={CASE04.irrigation.cycles} unit="回" caption={publicText("6バルブの近接記録を集約")} tone="green" icon={<ThunderboltOutlined />} />
              <MetricCard label={publicText("前後比較可能")} value={CASE04.irrigation.pairedEvents} unit="イベント" caption={publicText("−10〜0分と0〜+10分に画像あり")} tone="blue" icon={<FieldTimeOutlined />} />
              <MetricCard label={publicText("Aperture変化中央値")} value={CASE04.irrigation.medianApertureChange.toFixed(2)} unit="µm" caption={publicText("イベント単位の直前→直後")} tone="purple" icon={<AreaChartOutlined />} />
              <MetricCard label={publicText("Apertureが上昇")} value={CASE04.irrigation.apertureIncreaseEvents} unit={`/ ${CASE04.irrigation.pairedEvents}`} caption={publicText("一方向の共通応答ではない")} tone="orange" icon={<AimOutlined />} />
            </Reveal>

            <ScientificNote title={publicText("灌水の適否を判定する試験ではない")} tone="caution">
              <p>{publicText("\u6839\u57DF\u542B\u6C34\u7387\u3001\u6392\u6DB2\u91CF\u3001\u57F9\u5730EC\u3001\u6839\u57DF\u6E29\u5EA6\u3092\u540C\u6642\u306B\u6E2C\u5B9A\u3057\u3066\u3044\u306A\u3044\u305F\u3081\u3001 \u704C\u6C34\u306E\u9069\u5426\u3084\u52B9\u679C\u305D\u306E\u3082\u306E\u306F\u5224\u5B9A\u3067\u304D\u307E\u305B\u3093\u3002")}</p>
            </ScientificNote>
          </div>
        </section>

        <section id="table" className="science-section science-results-section case04-anchor">
          <div className="case-container">
            <SectionHeader index="13" eyebrow="INTEGRATED DATA TABLE" title={publicText("気孔形状・Open率・環境を1時間単位で統合。")} description={publicText("ページ内の主要な解析値を、同じ表で確認できます。")} />

            <DataTable caption={publicText("Table 1. 2026年1月21日 気孔・環境の1時間統合値")} columns={[{
            key: "timeLabel",
            label: "時間帯"
          }, {
            key: "sideLabel",
            label: "測定面"
          }, {
            key: "images",
            label: "画像",
            align: "right"
          }, {
            key: "stomata",
            label: "気孔",
            align: "right",
            render: v => formatNumber(v, 0)
          }, {
            key: "openRateText",
            label: "Open率",
            unit: "%",
            align: "right"
          }, {
            key: "apertureText",
            label: "Aperture",
            unit: "µm",
            align: "right"
          }, {
            key: "poreAreaText",
            label: "Pore Area",
            unit: "µm²",
            align: "right"
          }, {
            key: "aspectRatioText",
            label: "Short/Long",
            align: "right"
          }, {
            key: "solarText",
            label: "日射量",
            align: "right"
          }, {
            key: "temperatureText",
            label: "温度",
            unit: "°C",
            align: "right"
          }, {
            key: "vpdText",
            label: "VPD",
            unit: "kPa",
            align: "right"
          }, {
            key: "co2Text",
            label: "CO₂",
            unit: "ppm",
            align: "right"
          }]} rows={tableRows} />
          </div>
        </section>

        <section id="interpretation" className="science-section case04-anchor">
          <div className="study-content">
            <SectionHeader index="14" eyebrow="INTERPRETATION" title={publicText("大量データは、加工方法と限界を示して初めて意味を持つ。")} />

            <Reveal className="science-prose-columns">
              <div>
                <h3>{publicText("\u3053\u306E\u89E3\u6790\u304B\u3089\u8AAD\u307F\u53D6\u308C\u308B\u3053\u3068")}</h3>
                <ul>
                  <li>{publicText("Open\u7387\u3068\u5F62\u72B6\u6307\u6A19\u306F\u3001\u540C\u3058Open\u5FDC\u7B54\u306E\u5225\u5074\u9762\u3092\u6349\u3048\u308B\u3002")}</li>
                  <li>{publicText("\u753B\u50CF\u5358\u4F4D\u3067\u306F\u74B0\u5883\u3068\u306E\u95A2\u4FC2\u3068\u540C\u6642\u306B\u5927\u304D\u306A\u3070\u3089\u3064\u304D\u304C\u898B\u3048\u308B\u3002")}</li>
                  <li>{publicText("1\u6642\u9593\u96C6\u8A08\u3067\u306F\u65E5\u5468\u30C8\u30EC\u30F3\u30C9\u3068\u8FD1\u4F3C\u95A2\u4FC2\u304C\u660E\u77AD\u306B\u306A\u308B\u3002")}</li>
                  <li>{publicText("Aperture\u3001Area\u3001Short/Long\u3067\u74B0\u5883\u3068\u306E\u5BFE\u5FDC\u306E\u5F37\u3055\u304C\u7570\u306A\u308B\u3002")}</li>
                  <li>{publicText("\u983B\u56DE\u704C\u6C34\u4E0B\u3067\u3082\u3001\u500B\u3005\u306E\u704C\u6C34\u3078\u5358\u7D14\u306B\u540C\u671F\u3059\u308B\u5FDC\u7B54\u3067\u306F\u306A\u304B\u3063\u305F\u3002")}</li>
                </ul>
              </div>
              <div>
                <h3>{publicText("\u3053\u306E\u30C7\u30FC\u30BF\u3060\u3051\u3067\u306F\u8A00\u3048\u306A\u3044\u3053\u3068")}</h3>
                <ul>
                  <li>{publicText("\u6E29\u5EA6\u3001VPD\u3001\u65E5\u5C04\u306E\u3069\u308C\u304B\u4E00\u3064\u304C\u539F\u56E0\u3060\u3068\u306F\u7279\u5B9A\u3067\u304D\u306A\u3044\u3002")}</li>
                  <li>{publicText("\u5348\u524D\u3068\u5348\u5F8C\u306E\u9055\u3044\u3092\u6642\u523B\u3060\u3051\u306E\u52B9\u679C\u3068\u306F\u89E3\u91C8\u3067\u304D\u306A\u3044\u3002")}</li>
                  <li>{publicText("\u6A39\u306E\u6771\u9762\u3068\u897F\u9762\u306E\u5DEE\u3092\u4E00\u822C\u5316\u3067\u304D\u306A\u3044\u3002")}</li>
                  <li>{publicText("\u704C\u6C34\u304C\u9069\u5207\u3060\u3063\u305F\u304B\u3069\u3046\u304B\u306F\u5224\u5B9A\u3067\u304D\u306A\u3044\u3002")}</li>
                  <li>{publicText("1\u65E5\u5206\u306E\u7D50\u679C\u3092\u4ED6\u54C1\u7A2E\u30FB\u57F9\u5730\u30FB\u5B63\u7BC0\u3078\u4E00\u822C\u5316\u3067\u304D\u306A\u3044\u3002")}</li>
                </ul>
              </div>
            </Reveal>

            <ScientificNote title={publicText("次に追加したいデータ")} tone="success">
              <p>{publicText("\u6A39\u306E\u6771\u897F\u9762\u305D\u308C\u305E\u308C\u306E\u8449\u9762PPFD\u30FB\u8449\u6E29\u3001\u6839\u57DF\u542B\u6C34\u7387\u30FBEC\u3001\u6392\u6DB2\u3001 \u540C\u4E00\u8449\u306E\u53CD\u5FA9\u6E2C\u5B9A\u3001\u8907\u6570\u65E5\u306E\u518D\u73FE\u6027\u3092\u52A0\u3048\u308B\u3068\u3001\u7A7A\u9593\u30FB\u6642\u523B\u30FB\u6839\u57DF\u30FB\u5927\u6C17\u74B0\u5883\u306E\u5F71\u97FF\u3092\u3055\u3089\u306B\u5206\u96E2\u3067\u304D\u307E\u3059\u3002")}</p>
            </ScientificNote>
          </div>
        </section>

        <section id="method" className="science-section is-soft case04-method-section case04-anchor">
          <div className="case-container">
            <SectionHeader index="15" eyebrow="METHOD & DEFINITIONS" title={publicText("再計算・集計・環境同期・回帰の定義。")} description={publicText("ページ内の数値を再現するための方法情報です。")} />

            <MethodGrid items={[{
            label: "Aperture",
            value: "開口部の短軸長",
            note: "Open気孔のみ / µm"
          }, {
            label: "Pore Area",
            value: "開口領域の面積",
            note: "Open気孔のみ / µm²"
          }, {
            label: "Short / Long",
            value: "短軸長 ÷ 長軸長",
            note: "Open気孔のみ / 無次元"
          }, {
            label: "画像集計",
            value: "Open気孔の中央値",
            note: "Aperture・Area・Short/Longを画像ごとに算出"
          }, {
            label: "1時間集計",
            value: "画像中央値の平均",
            note: "Open率は全気孔を合算"
          }, {
            label: "環境同期",
            value: "最寄りの1分記録",
            note: "許容差2分以内"
          }, {
            label: "午前・午後回帰",
            value: "12時台を除外",
            note: "午前=樹東面 / 午後=樹西面 / 各n=5時間"
          }, {
            label: "画像単位回帰",
            value: "n=489",
            note: "12時台を除く有効画像"
          }]} />
          </div>
        </section>

        <section className="science-case-conclusion case04-conclusion">
          <div className="study-content">
            <Reveal>
              <RiseOutlined />
              <span>{publicText("FROM MEASUREMENT TO DECISION")}</span>
              <h2>{publicText("\u6E2C\u308B\u3060\u3051\u3067\u7D42\u308F\u3089\u305B\u306A\u3044\u3002\u5927\u91CF\u30C7\u30FC\u30BF\u3092\u3001\u683D\u57F9\u3067\u8AAD\u3081\u308B\u60C5\u5831\u3078\u3002")}</h2>
              <p>{publicText("\u6C17\u5B54\u5358\u4F4D\u306E\u691C\u51FA\u7D50\u679C\u3092\u3001\u753B\u50CF\u30FB\u6642\u9593\u30FB\u74B0\u5883\u30FB\u704C\u6C34\u3078\u3064\u306A\u3050\u3002 \u6307\u6A19\u3068\u7C92\u5EA6\u3092\u5909\u3048\u3066\u6BD4\u8F03\u3059\u308B\u3053\u3068\u3067\u3001\u690D\u7269\u304C\u3044\u3064\u3001\u3069\u306E\u3088\u3046\u306B\u5FDC\u7B54\u3057\u3066\u3044\u305F\u304B\u3092\u8AAD\u307F\u89E3\u304D\u307E\u3059\u3002")}</p>
              <div className="case04-conclusion-actions">
                <a href="https://happy-quality.notion.site/15771c88a48380c39416e9fa1bb2199d" target="_blank" rel="noopener noreferrer" className="case04-primary-link" onClick={() => trackEvent("case_study_cta_click", {
                case_study_id: "04",
                cta_type: "consultation"
              })}>{publicText("\u30C7\u30FC\u30BF\u89E3\u6790\u30FB\u6E2C\u5B9A\u76F8\u8AC7\u3092\u3059\u308B")}</a>
                <a href="/case-studies" className="case04-text-link">{publicText("Case Study\u4E00\u89A7\u3078 \u2192")}</a>
              </div>
            </Reveal>
          </div>
        </section>

        <section className="science-section case04-related-section">
          <div className="case-container">
            <SectionHeader index="16" eyebrow="CONTINUE EXPLORING" title={publicText("大量に測るCase Study 03から、解析するCase Study 04へ。")} description={publicText("処理、水分条件、大量計測、データ解析まで、利用場面を段階的に紹介しています。")} />
            <RelatedCases />
          </div>
        </section>

        <div className="case-container">
          <CaseNavigation previous={{
          id: "03",
          path: "/case-studies/03",
          title: "商業栽培トマト圃場での大量計測"
        }} />
        </div>
      </article>
    </PublicLayout>;
}
export default CaseStudy04;
